import React from 'react';
import { Alert, Card, Form, Radio, Typography } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  TRANSPORT_DIRECTIONS,
  transportDirectionLabelKey,
  type TransportDirection,
} from '../modules/transports/transportDirection';
import TransportFormSectionTitle from './TransportFormSectionTitle';

const { Text } = Typography;

interface TransportDirectionFieldProps {
  direction?: TransportDirection;
  supplierOrderCount: number;
  customerOrderCount: number;
  onTouched: () => void;
}

/**
 * WP14 (docs/decisions/2026-09-24-wp14-kierunek-transportu-i-odbior-wlasny.md):
 * the first decision on a truck — who pays for it. Always visible (not in a
 * collapsed section): the direction decides whether the freight goes into the
 * ZD batches or onto the customer orders. The money rule itself is the
 * backend's; this only explains it next to the choice.
 */
const TransportDirectionField: React.FC<TransportDirectionFieldProps> = ({
  direction,
  supplierOrderCount,
  customerOrderCount,
  onTouched,
}) => {
  const { t } = useTranslation(['transport']);
  const inbound = direction === 'INBOUND';

  return (
    <Card
      className="transport-form-section transport-direction-field"
      data-testid="transport-direction-field"
      styles={{ body: { padding: '12px 16px' } }}
    >
      <TransportFormSectionTitle icon={<SwapOutlined />} label={t('transport:direction.label')} />
      <Form.Item
        name="direction"
        rules={[{ required: true, message: t('transport:direction.required') }]}
        style={{ margin: '8px 0 0' }}
      >
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          onChange={onTouched}
          aria-label={t('transport:direction.label')}
          options={TRANSPORT_DIRECTIONS.map((value) => ({
            value,
            label: t(transportDirectionLabelKey(value)),
          }))}
        />
      </Form.Item>
      {direction ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          {t(inbound ? 'transport:direction.hint.INBOUND' : 'transport:direction.hint.OUTBOUND')}
        </Text>
      ) : null}
      {inbound && supplierOrderCount === 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={t('transport:direction.inboundWithoutSupplierOrders')}
        />
      ) : null}
      {inbound && supplierOrderCount > 0 && customerOrderCount > 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 8 }}
          message={t('transport:direction.inboundWithCustomerOrders')}
        />
      ) : null}
      {!inbound && direction && supplierOrderCount > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={t('transport:direction.outboundWithSupplierOrders')}
        />
      ) : null}
    </Card>
  );
};

export default TransportDirectionField;
